import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Home as HomeIcon, Server, Headphones, Mail, Layers } from 'lucide-react';
import Logo from './devNectar_new_logo.png';
import './Navbar.css';
import Swal from 'sweetalert2';

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: 'Home',       href: '/', icon: <HomeIcon className="w-4 h-4" /> },
    { name: 'Services',   href: '/services', icon: <Server className="w-4 h-4" /> },
    { name: 'Portfolio',  href: '/portfolio', icon: <Layers className="w-4 h-4" /> },
    // { name: 'Careers',    href: '/careers', icon: <Briefcase className="w-4 h-4" /> },
    { name: 'Support',    href: '/support', icon: <Headphones className="w-4 h-4" /> },
    { name: 'Contact Us', href: '/contact', icon: <Mail className="w-4 h-4" /> },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav data-id="main-navbar" className="bg-white shadow-sm sticky top-0 z-50 border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">

          {/* Logo */}
          <Link to="/" data-id="navbar-logo" className="flex items-center">
            <img src={Logo} alt="logo" loading="lazy" className="h-[55px] w-auto object-contain" />
          </Link>

          {/* Desktop nav */}
          <div data-id="desktop-nav" className="hidden md:block">
            <div className="flex items-center space-x-1">
              {navigation.map((item) => {
                const active = isActive(item.href);
                const isSupport = item.name === 'Support';

                const handleNavClick = (e: React.MouseEvent) => {
                  if (isSupport) {
                    e.preventDefault();
                    Swal.fire({
                      icon: 'info',
                      title: 'Greetings! 👋',
                      text: 'Our dedicated Support portal is coming soon. Stay tuned!',
                      confirmButtonColor: '#3b82f6',
                      width: 360,
                      padding: '1.5em',
                      customClass: {
                        popup: 'rounded-2xl',
                        icon: '!scale-75 !mt-2 !mb-2',
                        title: '!text-xl !mt-0',
                        confirmButton: 'rounded-xl px-8 py-2 font-semibold focus:ring-2 focus:ring-blue-500'
                      }
                    });
                  }
                };

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={handleNavClick}
                    data-id={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                    className={`relative px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 group flex items-center gap-2
                      ${active
                        ? 'text-blue-700 bg-blue-50'
                        : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
                      }
                    `}
                  >
                    <div className="relative flex items-center justify-center group/icon">
                      <span className={`
                        relative z-10 transition-all duration-500 transform
                        ${active ? 'text-blue-600 scale-110' : 'text-gray-500 group-hover:text-blue-600 group-hover:scale-125 group-hover:-translate-y-1 icon-elastic-lift'}
                      `}>
                        {item.icon}
                      </span>
                      <span className={`
                        absolute inset-[-6px] rounded-full transition-all duration-500
                        ${active ? 'bg-blue-50 scale-100 opacity-100' : 'bg-blue-50 scale-0 opacity-0 group-hover:scale-100 group-hover:opacity-100'}
                      `} />
                    </div>
                    <span className={`relative z-10 ml-1 transition-colors duration-300 ${active ? 'text-blue-700 font-bold' : 'text-gray-600 group-hover:text-blue-700 font-semibold'}`}>
                      {item.name}
                    </span>
                    {/* Active underline gradient */}
                    <span
                      className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-[2px] rounded-full bg-gradient-to-r from-blue-500 to-blue-700 transition-all duration-300
                        ${active ? 'w-4/5 opacity-100' : 'w-0 opacity-0 group-hover:w-1/2 group-hover:opacity-40'}
                      `}
                    />
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Mobile menu button */}
          <div data-id="mobile-menu-button" className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
            >
              {isOpen ? <X className="block h-6 w-6" /> : <Menu className="block h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div data-id="mobile-menu" className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white shadow-lg border-t border-gray-100">
            {navigation.map((item) => {
              const active = isActive(item.href);
              const isSupport = item.name === 'Support';

              const handleMobileNavClick = (e: React.MouseEvent) => {
                if (isSupport) {
                  e.preventDefault();
                  setIsOpen(false);
                  Swal.fire({
                    icon: 'info',
                    title: 'Greetings! 👋',
                    text: 'Our dedicated Support portal is coming soon. Stay tuned!',
                    confirmButtonColor: '#3b82f6',
                    width: 360,
                    padding: '1.5em',
                    customClass: {
                      popup: 'rounded-2xl',
                      icon: '!scale-75 !mt-2 !mb-2',
                      title: '!text-xl !mt-0',
                      confirmButton: 'rounded-xl px-8 py-2 font-semibold focus:ring-2 focus:ring-blue-500'
                    }
                  });
                } else {
                  setIsOpen(false);
                }
              };

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  data-id={`mobile-nav-${item.name.toLowerCase().replace(' ', '-')}`}
                  onClick={handleMobileNavClick}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-md text-base font-medium transition-all
                    ${active
                      ? 'text-blue-700 bg-blue-50 border-l-2 border-blue-600'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-blue-600'
                    }
                  `}
                >
                  <span className={`
                    w-8 h-8 flex items-center justify-center rounded-full transition-all duration-300
                    ${active ? 'bg-blue-100 text-blue-600' : 'text-gray-500 group-hover:text-blue-600 group-hover:bg-blue-50'}
                  `}>
                    {item.icon}
                  </span>
                  <span className={active ? 'font-bold' : 'font-medium'}>{item.name}</span>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;